import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { crm } from './crmApi';
import type { CrmAuth, ShiftRequest, ShiftsData } from './crmApi';
import { Empty, ShiftChip, Spinner, Tabs, fmtHm, fmtWhen, forgetCrmMe, inputCls, labelCls, purpleBg, shiftText } from './ui';
import { dayLabel } from './viz';

// ── Shifts ───────────────────────────────────────────────────
// Two shifts a day. The Lead Manager (or the admin) sets their times and puts
// each salesperson on one; every Monday everyone swaps. A salesperson who
// needs the other shift in a week asks for it, and a manager approves.
// New leads go only to whoever is on shift. Mirrors the app's crm_shift_screens.dart.

const weekName = (monday: string, d: ShiftsData) => (monday === d.thisWeek ? 'This week' : monday === d.nextWeek ? 'Next week' : `Week of ${dayLabel(monday)}`);

const STATUS_CLS: Record<ShiftRequest['status'], string> = {
  Pending: 'bg-amber-100 text-amber-800', Approved: 'bg-green-100 text-green-800', Rejected: 'bg-red-100 text-red-700',
};
const STATUS_TEXT: Record<ShiftRequest['status'], string> = { Pending: 'Waiting for approval', Approved: 'Approved', Rejected: 'Not approved' };

// One request with what it asks for; managers get the buttons
const RequestCard: React.FC<{ r: ShiftRequest; d: ShiftsData | null; onDecide?: (r: ShiftRequest, approve: boolean, note: string) => Promise<void>; showWho?: boolean }> = ({ r, d, onDecide, showWho }) => {
  const [turning, setTurning] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const decide = async (approve: boolean) => {
    if (!onDecide) return;
    setBusy(true);
    try { await onDecide(r, approve, note.trim()); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-4 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        {showWho && <span className="font-bold text-gray-800">{r.byName}</span>}
        <span className="text-sm text-gray-700">{d ? weekName(r.week, d) : `Week of ${dayLabel(r.week)}`}: <b>{r.fromName}</b> → <b>{r.toName}</b></span>
        {r.status !== 'Pending' || !onDecide ? <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_CLS[r.status]}`}>{STATUS_TEXT[r.status]}</span> : null}
      </div>
      {r.reason && <p className="text-xs text-gray-600">“{r.reason}”</p>}
      <p className="text-[11px] text-gray-400">Asked {fmtWhen(r.createdAt)}{r.decidedByName ? ` · ${r.status === 'Approved' ? 'approved' : 'turned down'} by ${r.decidedByName}` : ''}{r.note ? `: ${r.note}` : ''}</p>
      {onDecide && r.status === 'Pending' && (turning ? (
        <div className="flex flex-col sm:flex-row gap-2">
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Why not (they will see this)" className={inputCls} autoFocus aria-label="Why not" />
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={() => decide(false)} disabled={busy} className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50">{busy ? 'Saving…' : 'Turn down'}</button>
            <button onClick={() => setTurning(false)} className="px-4 py-2 rounded-xl text-xs font-bold border border-gray-200 text-gray-600">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <button onClick={() => decide(true)} disabled={busy} className="px-4 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-50" style={purpleBg}>{busy ? 'Approving…' : `Approve: ${r.toName}`}</button>
          <button onClick={() => setTurning(true)} disabled={busy} className="px-4 py-2 rounded-xl text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50">Turn down</button>
        </div>
      ))}
    </div>
  );
};

// ── The manager's Shifts page ────────────────────────────────
export const CrmShiftsManager: React.FC<{ auth: CrmAuth }> = ({ auth }) => {
  const [d, setD] = useState<ShiftsData | null>(null);
  const [times, setTimes] = useState<{ name: string; start: string; end: string }[]>([]);
  const [rotate, setRotate] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState('');
  const fill = (r: ShiftsData) => { setD(r); setTimes(r.shifts.map(s => ({ name: s.name, start: s.start, end: s.end }))); setRotate(r.rotate); };
  const load = useCallback(async () => {
    try { fill(await crm.shifts(auth)); } catch (e: any) { toast.error(e.message); }
  }, [auth]);
  useEffect(() => { load(); }, [load]);

  const saveTimes = async () => {
    setSaving(true);
    try {
      await crm.saveSettings(auth, { shifts: times.map((t, i) => ({ key: (['A', 'B'] as const)[i], ...t })), shiftRotate: rotate });
      forgetCrmMe();
      toast.success('Shift times saved');
      load();
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const set = async (id: string, shift: '' | 'A' | 'B', from: 'this' | 'next') => {
    setBusy(`${id}:${from}`);
    try {
      fill(await crm.setShift(auth, id, shift, from));
      toast.success('Shift changed. They get a notification.');
    } catch (e: any) { toast.error(e.message); }
    finally { setBusy(''); }
  };

  const decide = async (r: ShiftRequest, approve: boolean, note: string) => {
    try {
      await crm.decideShiftRequest(auth, r.id, approve, note);
      toast.success(approve ? `${r.byName}: ${r.toName} from ${d ? weekName(r.week, d).toLowerCase() : 'that week'}` : `Turned down. ${r.byName} will be told.`);
      load();
    } catch (e: any) { toast.error(e.message); load(); }
  };

  if (!d) return <Spinner label="Loading the shifts…" />;
  const takers = (d.team || []).filter(t => t.takesLeads);
  const others = (d.team || []).filter(t => !t.takesLeads);
  const changed = JSON.stringify(times) !== JSON.stringify(d.shifts.map(s => ({ name: s.name, start: s.start, end: s.end }))) || rotate !== d.rotate;
  const pick = (t: typeof takers[number], week: 'this' | 'next') => {
    const cur = (week === 'this' ? t.thisWeek : t.nextWeek)?.key || '';
    return (
      <select value={cur} disabled={busy === `${t.id}:${week}`} onChange={e => set(t.id, e.target.value as '' | 'A' | 'B', week)}
        className={`${inputCls} !py-2 min-w-[150px]`} aria-label={`${t.name}, ${week} week`}>
        {d.shifts.map(s => <option key={s.key} value={s.key}>{s.name} · {fmtHm(s.start)}–{fmtHm(s.end)}</option>)}
        <option value="">Whole day (no shift)</option>
      </select>
    );
  };

  return (
    <div className="space-y-5">
      {d.requests.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-bold text-gray-800 px-1">🔄 Shift requests waiting · {d.requests.length}</h2>
          {d.requests.map(r => <RequestCard key={r.id} r={r} d={d} onDecide={decide} showWho />)}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        <div>
          <p className="font-bold text-gray-800">Who is on which shift</p>
          <p className="text-xs text-gray-400 mt-0.5">Pick a shift and it is saved. Every Monday they swap to the other one{d.rotate ? '' : ' (swapping is off now)'}. New leads go only to whoever is on shift.</p>
        </div>
        {takers.length === 0 ? <p className="text-sm text-gray-400">Nobody is taking leads.</p> : (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-sm min-w-[560px]">
              <thead><tr className="text-xs text-gray-500">
                <th className="text-left py-2 px-1 font-bold">Salesperson</th>
                <th className="text-left py-2 px-1 font-bold">This week · {dayLabel(d.thisWeek)}</th>
                <th className="text-left py-2 px-1 font-bold">Next week · {dayLabel(d.nextWeek)}</th>
              </tr></thead>
              <tbody>
                {takers.map(t => (
                  <tr key={t.id} className="border-t border-gray-100">
                    <td className="py-2.5 px-1">
                      <p className="font-bold text-gray-800">{t.name}</p>
                      <p className="text-[11px] text-gray-400 flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${t.onNow ? 'bg-green-500' : 'bg-gray-300'}`} />{t.onNow ? 'On shift now' : 'Not on shift now'}{t.role !== 'Salesperson' ? ` · ${t.role}` : ''}
                      </p>
                    </td>
                    <td className="py-2.5 px-1">{pick(t, 'this')}</td>
                    <td className="py-2.5 px-1">{pick(t, 'next')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {others.length > 0 && <p className="text-[11px] text-gray-400">Not taking new leads: {others.map(o => o.name).join(', ')}. A Lead Manager still calls the leads the salespeople couldn't reach, all working day; with "New leads too" they show up here.</p>}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        <div>
          <p className="font-bold text-gray-800">Shift times</p>
          <p className="text-xs text-gray-400 mt-0.5">Inside the working day ({fmtHm(d.workStart)} – {fmtHm(d.workEnd)}; change it on Rules).</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {times.map((t, i) => (
            <div key={i} className="rounded-xl border border-gray-100 p-3 space-y-2">
              <div><label className={labelCls} htmlFor={`s-name-${i}`}>Name</label><input id={`s-name-${i}`} value={t.name} maxLength={30} onChange={e => setTimes(ts => ts.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} className={inputCls} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className={labelCls} htmlFor={`s-start-${i}`}>Starts</label><input id={`s-start-${i}`} type="time" value={t.start} onChange={e => setTimes(ts => ts.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} className={inputCls} /></div>
                <div><label className={labelCls} htmlFor={`s-end-${i}`}>Ends</label><input id={`s-end-${i}`} type="time" value={t.end} onChange={e => setTimes(ts => ts.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} className={inputCls} /></div>
              </div>
            </div>
          ))}
        </div>
        <label className="flex items-center gap-3 cursor-pointer w-fit">
          <input type="checkbox" checked={rotate} onChange={e => setRotate(e.target.checked)} className="w-4 h-4 accent-purple-700" />
          <span className="text-sm text-gray-700"><b>Swap shifts every Monday</b> <span className="text-gray-400">(turn off to keep everyone on the same shift)</span></span>
        </label>
        <button onClick={saveTimes} disabled={saving || !changed} className="px-5 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50" style={purpleBg}>{saving ? 'Saving…' : 'Save shift times'}</button>
      </div>
    </div>
  );
};

// ── A salesperson's shift, and asking for the other one ──────
export const CrmMyShift: React.FC<{ auth: CrmAuth }> = ({ auth }) => {
  const [d, setD] = useState<ShiftsData | null>(null);
  const [asking, setAsking] = useState(false);
  const [week, setWeek] = useState<'this' | 'next'>('next');
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);
  const load = useCallback(async () => {
    try { setD(await crm.shifts(auth)); } catch (e: any) { toast.error(e.message); }
  }, [auth]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!reason.trim()) { toast.error('Say why you need the other shift'); return; }
    setSending(true);
    try {
      const r = await crm.requestShift(auth, week, reason.trim());
      toast.success(`Asked for ${r.request.toName}. You get a notification when it is decided.`);
      setAsking(false); setReason('');
      load();
    } catch (e: any) { toast.error(e.message); }
    finally { setSending(false); }
  };

  if (!d) return <Spinner />;
  const me = d.me;
  const pick = week === 'this' ? me?.thisWeek : me?.nextWeek;
  const other = pick ? d.shifts.find(s => s.key !== pick.key) : null;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="font-bold text-gray-800">My shift</p>
          <p className="text-xs text-gray-400 mt-0.5">{d.rotate ? 'Shifts swap every Monday.' : 'Everyone stays on their shift.'} New leads come to you during your shift.</p>
        </div>
        {me && (me.thisWeek || me.nextWeek) && !asking && (
          <button onClick={() => setAsking(true)} className="px-4 py-2 rounded-xl text-xs font-bold border-2 border-purple-200 text-purple-700 hover:bg-purple-50">Ask for the other shift</button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-xl bg-gray-50 px-4 py-3">
          <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">This week · {dayLabel(d.thisWeek)}</p>
          <div className="mt-1.5"><ShiftChip shift={me?.thisWeek || null} onNow={me?.onNow} /></div>
        </div>
        <div className="rounded-xl bg-gray-50 px-4 py-3">
          <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Next week · {dayLabel(d.nextWeek)}</p>
          <div className="mt-1.5"><ShiftChip shift={me?.nextWeek || null} /></div>
        </div>
      </div>
      {me && !me.thisWeek && !me.nextWeek && <p className="text-xs text-gray-500">You are not on a shift: you work the whole day. The Lead Manager puts you on one.</p>}
      {asking && (
        <div className="rounded-xl border-2 border-purple-100 bg-purple-50/40 p-4 space-y-3">
          <div>
            <p className={labelCls}>Which week?</p>
            <div className="flex gap-2" role="radiogroup" aria-label="Which week">
              {(['this', 'next'] as const).map(w => (
                <button key={w} type="button" role="radio" aria-checked={week === w} onClick={() => setWeek(w)} className="flex-1 px-3 py-2 rounded-xl border-2 text-left"
                  style={week === w ? { background: '#2b2240', color: '#fff', borderColor: '#2b2240' } : { background: '#fff', color: '#374151', borderColor: '#e5e7eb' }}>
                  <span className="block text-sm font-bold">{w === 'this' ? 'This week' : 'Next week'}</span>
                  <span className="block text-[10px] opacity-70">{shiftText((w === 'this' ? me?.thisWeek : me?.nextWeek) || null)}</span>
                </button>
              ))}
            </div>
          </div>
          {other && <p className="text-sm text-gray-700">You ask for <b>{shiftText(other)}</b> instead of {pick?.name}.</p>}
          <div>
            <label className={labelCls} htmlFor="shift-why">Why?</label>
            <input id="shift-why" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. college exam in the mornings" className={inputCls} />
          </div>
          <div className="flex gap-2">
            <button onClick={send} disabled={sending} className="px-4 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-50" style={purpleBg}>{sending ? 'Sending…' : 'Send shift request'}</button>
            <button onClick={() => setAsking(false)} className="px-4 py-2 rounded-xl text-xs font-bold border border-gray-200 text-gray-600">Cancel</button>
          </div>
        </div>
      )}
      {d.requests.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">My shift requests</p>
          {d.requests.map(r => <RequestCard key={r.id} r={r} d={d} />)}
        </div>
      )}
    </div>
  );
};

// ── Shift requests for the Requests page (managers) ──────────
export const CrmShiftRequests: React.FC<{ auth: CrmAuth; onChanged?: () => void }> = ({ auth, onChanged }) => {
  const [status, setStatus] = useState<'Pending' | 'Approved' | 'Rejected'>('Pending');
  const [data, setData] = useState<Awaited<ReturnType<typeof crm.shiftRequests>> | null>(null);
  const load = useCallback(async () => {
    try { setData(await crm.shiftRequests(auth, status)); } catch (e: any) { toast.error(e.message); }
  }, [auth, status]);
  useEffect(() => { load(); }, [load]);
  const decide = async (r: ShiftRequest, approve: boolean, note: string) => {
    try {
      await crm.decideShiftRequest(auth, r.id, approve, note);
      toast.success(approve ? `${r.byName} is on ${r.toName} from that week` : `Turned down. ${r.byName} will be told.`);
      load(); onChanged?.();
    } catch (e: any) { toast.error(e.message); load(); }
  };
  return (
    <div className="space-y-3">
      <Tabs label="Status" value={status} onChange={v => setStatus(v as typeof status)}
        tabs={[['Pending', 'Waiting'], ['Approved', 'Approved'], ['Rejected', 'Turned down']].map(([id, label]) => ({ id, label: `${label}${data ? ` ${data.counts[id as typeof status]}` : ''}` }))} />
      {!data ? <Spinner /> : data.requests.length === 0 ? (
        <Empty icon={status === 'Pending' ? '✅' : '📭'} title={status === 'Pending' ? 'No shift requests waiting' : 'None yet'} sub="Salespeople ask for the other shift on their Requests page." />
      ) : data.requests.map(r => <RequestCard key={r.id} r={r} d={null} onDecide={decide} showWho />)}
    </div>
  );
};
